import { Icon } from "@mg/dali/src";
import {
  CaretRight,
  ChatCircle,
  Gear,
  House,
  Kanban,
  Minus,
  Plus,
  Trash,
  UserCircle,
} from "@phosphor-icons/react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  IconButton,
  Link,
  Text,
} from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import {
  Link as NavLink,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import { FolderTreeView } from "./AppDrawer";
import { AvatarWithInitials } from "./AvatarWithInitials";
import { EditProfileDialog } from "./EditProfileDialog";
import EnterpriseSwitcher from "./EnterpriseSwitcher";
import { Sidebar } from "./sidebar/Sidebar";
import { SidebarContent } from "./sidebar/SidebarContent";
import { SidebarFooter } from "./sidebar/SidebarFooter";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { SidebarMenuButton } from "./sidebar/SidebarMenuButton";
import { SidebarMenuItem } from "./sidebar/SidebarMenuItem";
import { SidebarProvider } from "./sidebar/SidebarProvider";
import { SidebarRail } from "./sidebar/SidebarRail";
import { SidebarTrigger } from "./sidebar/SidebarTrigger";
import { useSidebar } from "./sidebar/useSidebar";

import { ReactComponent as Logo } from "../images/Logo-blue.svg";
import { ReactComponent as Wordmark } from "../images/Logotype.svg";
import { askPunttChatRoute } from "../routes/ask-puntt/chatRoute";
import { askPunttProjectsRoute } from "../routes/ask-puntt/projectsRoute";
import { askPunttRoute } from "../routes/ask-puntt/route";
import { indexRoute } from "../routes/index/route";
import { roiRoute } from "../routes/roi/route";
import { archivedUsersRoute } from "../routes/settings/routes/archivedUsersRoute";
import { integrationSettingsRoute } from "../routes/settings/routes/integrationSettingsRoute";
import { knowledgeDatabaseRoute } from "../routes/settings/routes/knowledge-database/route";
import { knowledgeSettingRoute } from "../routes/settings/routes/knowledgeSettingRoute";
import { usersRoute } from "../routes/settings/routes/usersRoute";
import { ticketsRoute } from "../routes/tickets/route";
import { getGuidelines } from "../services/brand-guidelines";
import { useAnalytics } from "../utils/analytics";
import {
  canAccessAskPuntt,
  canManageBrandGuidelines,
  canAccessKnowledgeDatabase,
  canManageUsers,
  canAccessPunttDashboard,
  isMeaningfulGigsRole,
} from "../utils/auth";
import { getNameInitials } from "../utils/constants";
import { isNil } from "../utils/fp";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { assetForUser } from "../utils/imageHandler";
import {
  useAskPunttChats,
  useCreateAskPunttChat,
  useDeleteAskPunttChat,
} from "../utils/queries/askPuntt";
import { logout } from "../utils/slices/auth";
import { setDrawerOpen } from "../utils/slices/ui";

const navLinkClasses =
  "transition-colors [&_button]:transition-colors [&_button]:hover:bg-puntt-neutral-grayA-2";
const activeNavLinkClasses =
  "[&_button]:!bg-puntt-neutral-grayA-3 [&_button]:hover:!bg-puntt-neutral-grayA-4 [&_button]:active:!bg-puntt-neutral-grayA-5";

// Shared explicit footprint for the three footer trigger cards (settings,
// login, account). They MUST share the same outer dimensions: in the
// collapsed icon rail (`calc(3.5rem + 1px)` = 57px) the SidebarFooter `p-4`
// leaves a 25px-wide button area, so any card wider than 24px (e.g. the
// previous `size-7` = 28px) overflows and is visibly clipped on the right
// by `SidebarMenuButton`'s `overflow-hidden`. Keeping all three cards at
// `size-6` (24px) matches the Radix Avatar `size="1"` footprint, fits
// cleanly in both collapsed and expanded rail widths, and gives the three
// cards a consistent vertical rhythm.
export const SIDEBAR_TRIGGER_CARD_SIZE_CLASS = "size-6";

export function AppSidebar() {
  const posthog = usePostHog();
  const analytics = useAnalytics("AppSidebar");
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value)!;
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  return (
    <SidebarProvider
      name="app"
      open={sidebarOpen}
      onOpenChange={(open) => {
        analytics.capture(`sidebar_${open ? "opened" : "closed"}`);
        dispatch(setDrawerOpen({ app: open }));
      }}
    >
      <Sidebar data-testid="app-sidebar">
        <AppSidebarHeader />

        <SidebarContent data-testid="app-sidebar-content">
          <Flex direction="column" className="min-h-0 flex-1 overflow-hidden">
            <SidebarGroup className="shrink-0 pb-0">
              <SidebarMenu>
                <SidebarMenuItem
                  data-testid="sidebar-menu-item-roi-dashboard"
                  data-auth-trigger="roi-dashboard"
                  hidden={!isMeaningfulGigsRole()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={roiRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              ROI Dashboard
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        ROI Dashboard
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-dashboard"
                  data-auth-trigger="dashboard"
                  hidden={!canAccessPunttDashboard()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={indexRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Home
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Home
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-ask-puntt"
                  data-auth-trigger="ask-puntt"
                  hidden={!canAccessAskPuntt()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={askPunttRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <ChatCircle
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Ask Puntt
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <ChatCircle
                            size={18}
                            color="rgb(var(--puntt-blue-12))"
                          />
                        </Box>
                        Ask Puntt
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>

            <AskPunttProjects />
            <AskPunttRecents />

            <SidebarGroup className="shrink-0 pb-0">
              <SidebarMenu>
                <SidebarMenuItem
                  data-testid="sidebar-menu-item-projects"
                  data-auth-trigger="projects"
                  hidden={canAccessPunttDashboard()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={ticketsRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <Kanban
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Documents
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <Kanban size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Documents
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>

            <FolderTreeView />
          </Flex>
        </SidebarContent>

        <SidebarFooter data-testid="app-sidebar-footer">
          <SidebarMenu>
            <SidebarMenuItem
              hidden={
                !canAccessKnowledgeDatabase() &&
                !canManageBrandGuidelines() &&
                !canManageUsers()
              }
            >
              <DropdownMenu.Root
                open={isSettingsOpen}
                onOpenChange={setIsSettingsOpen}
              >
                <DropdownMenu.Trigger
                  className="group/dropdown-menu"
                  data-testid="sidebar-settings-trigger"
                >
                  <SidebarMenuButton className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center">
                    <span
                      aria-hidden
                      data-testid="sidebar-settings-trigger-card"
                      className="flex size-6 shrink-0 items-center justify-center rounded-md border border-puntt-neutral-gray-5 bg-base-white shadow-sm transition-all group-hover/menu-button:border-puntt-neutral-gray-7 group-hover/menu-button:bg-puntt-neutral-gray-3 group-hover/menu-button:shadow-md group-data-[state=open]/dropdown-menu:border-puntt-neutral-gray-7 group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Gear size={14} className="text-puntt-neutral-gray-11" />
                    </span>

                    <Flex
                      width="100%"
                      className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Settings
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content side="right">
                  <DropdownMenu.Item
                    hidden={!canManageBrandGuidelines()}
                    data-testid="sidebar-settings-knowledge"
                  >
                    <NavLink
                      to={knowledgeSettingRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Knowledge Settings
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item hidden={!canAccessKnowledgeDatabase()}>
                    <NavLink
                      to={knowledgeDatabaseRoute.to}
                      search={{ pageSize: 100 }}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Knowledge Database
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageUsers()}
                    data-testid="sidebar-settings-integration"
                  >
                    <NavLink
                      to={integrationSettingsRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Integration Settings
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageUsers()}
                    data-testid="sidebar-settings-manage-users"
                  >
                    <NavLink
                      to={usersRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Manage Users
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item hidden={!canManageUsers()}>
                    <NavLink
                      to={archivedUsersRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Archived Users
                    </NavLink>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </SidebarMenuItem>

            <SidebarMenuItem
              hidden={!isNil(user.email)}
              data-auth-trigger="login-signup-button"
            >
              <SidebarMenuButton className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center">
                <span
                  aria-hidden
                  data-testid="sidebar-login-trigger-card"
                  className="flex size-6 shrink-0 items-center justify-center rounded-md border border-puntt-blue-7 bg-base-white text-puntt-blue-11 shadow-sm transition-all group-hover/menu-button:border-puntt-blue-9 group-hover/menu-button:bg-puntt-blue-2 group-hover/menu-button:shadow-md"
                >
                  <UserCircle size={14} />
                </span>

                <Flex
                  width="100%"
                  className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden"
                >
                  <Text
                    color="blue"
                    size="1"
                    weight="medium"
                    className="flex-1 whitespace-nowrap text-left"
                  >
                    Log In or Sign Up
                  </Text>
                </Flex>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem hidden={isNil(user.email)}>
              <DropdownMenu.Root
                open={isAccountOpen}
                onOpenChange={setIsAccountOpen}
              >
                <DropdownMenu.Trigger
                  className="group/dropdown-menu"
                  data-testid="sidebar-account-trigger"
                >
                  <SidebarMenuButton className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center">
                    <Avatar
                      variant="solid"
                      size="1"
                      radius="full"
                      src={assetForUser(user.avatar)}
                      fallback={
                        getNameInitials(user.name) ?? (
                          // Match the outlined `Icon.User` silhouette used by
                          // AvatarWithInitials on the Manage Users page so
                          // the no-name affordance is visually consistent.
                          <Icon.User size={16} />
                        )
                      }
                      data-testid="sidebar-account-trigger-card"
                      className="size-6 shrink-0 border border-puntt-neutral-gray-5 shadow-sm transition-all group-hover/menu-button:border-puntt-neutral-gray-7 group-hover/menu-button:shadow-md group-data-[state=open]/dropdown-menu:border-puntt-neutral-gray-7"
                    />

                    <Flex
                      width="100%"
                      className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Account
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content side="right" className="min-w-[220px]">
                  {/* Header area */}
                  <div className="flex flex-col items-center py-4">
                    <AvatarWithInitials
                      size={16}
                      name={user.name}
                      avatar={user.avatar}
                    />
                    <span className="mt-2 text-center text-lg font-semibold">
                      {user.name}
                    </span>
                    <span className="text-gray-500 mt-1 text-center text-sm">
                      {user.email}
                    </span>
                  </div>
                  <div className="border-gray-200 my-2 border-t" />
                  <DropdownMenu.Item
                    onClick={() => {
                      setIsEditProfileOpen(true);
                      setIsAccountOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    Edit Profile
                  </DropdownMenu.Item>
                  <div className="border-gray-200 my-2 border-t" />
                  <DropdownMenu.Item
                    onClick={() => {
                      dispatch(logout());
                      posthog.reset();
                      setIsAccountOpen(false);
                    }}
                    className="text-red-600 cursor-pointer"
                    data-testid="sidebar-account-logout"
                  >
                    Log Out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail data-testid="app-sidebar-rail" />
        <SidebarTrigger data-testid="app-sidebar-trigger" />
      </Sidebar>

      <EditProfileDialog
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />
    </SidebarProvider>
  );
}

function AppSidebarHeader() {
  const user = useAppSelector((state) => state.auth.value);
  const { open } = useSidebar();

  return (
    <SidebarHeader
      data-testid="app-sidebar-header"
      data-auth-trigger="sidebar-header"
    >
      <Flex gap="4" justify={open ? "between" : "center"} align="center">
        <NavLink to={indexRoute.to}>
          {open ? (
            <Wordmark className="h-4 max-w-max" />
          ) : (
            <Logo className="size-6" />
          )}
        </NavLink>

        {open && !isNil(user?.email) ? <EnterpriseSwitcher /> : null}
      </Flex>
    </SidebarHeader>
  );
}

function projectConfidenceColor(confidence: number | undefined): string {
  if (confidence == null) return "bg-puntt-neutral-gray-8";
  if (confidence >= 80) return "bg-malachite-500";
  if (confidence >= 60) return "bg-puntt-gold-9";
  return "bg-puntt-red-9";
}

function AskPunttProjects() {
  const { data } = useAskPunttChats();
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const isOnAskPuntt = matches.some((m) => m.pathname.includes("/ask-puntt"));
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const { data: guidelines } = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const enabledSkillIds = guidelines?.askPunttSkills
    ?.filter((s) => s.enabled)
    .map((s) => s.skillId);
  const showProjects =
    !enabledSkillIds || enabledSkillIds.includes("project_workflow");

  if (!canAccessAskPuntt() || !showProjects || !open || !isOnAskPuntt)
    return null;

  const projects =
    data?.chats?.filter((c) => c.workflowType === "project" && c.projectData) ??
    [];
  if (projects.length === 0) return null;

  const handleNewProject = async () => {
    try {
      const result = await createChat.mutateAsync({
        message: "I want to set up a new project",
        workflowType: "project",
      });
      navigate({
        to: askPunttChatRoute.to,
        params: { chatId: result.chat._id },
      });
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  return (
    <SidebarGroup className="pb-0">
      <Flex
        className="flex w-full items-center gap-4 rounded-md px-2 py-1.5"
        style={{ background: "inherit" }}
        data-testid="sidebar-ask-puntt-projects-header"
      >
        <Text size="2" weight="bold" className="flex-1 truncate px-0">
          Projects
        </Text>
        <Link size="1" className="puntt-link shrink-0" asChild>
          <NavLink
            to={askPunttProjectsRoute.to}
            className="text-puntt-neutral-gray-9 transition-colors hover:text-puntt-accent-9"
            data-testid="sidebar-ask-puntt-projects-all-link"
          >
            <Text size="1">All projects →</Text>
          </NavLink>
        </Link>
      </Flex>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            className="pl-3 text-puntt-accent-9 transition-colors hover:!bg-puntt-accent-2"
            onClick={handleNewProject}
            disabled={createChat.isPending}
          >
            <span className="flex items-center gap-2">
              <Plus size={14} weight="bold" className="shrink-0" />
              <Text size="1" weight="medium">
                New project
              </Text>
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
        {projects.slice(0, 8).map((chat) => {
          // Treat as own until auth resolves so own-chat affordances don't
          // flash off during the brief window before currentUserId loads.
          const isOwnChat =
            currentUserId === undefined || chat.userId === currentUserId;
          return (
            <SidebarMenuItem key={chat._id}>
              <Link size="2" className="puntt-link" asChild>
                <NavLink
                  to={askPunttChatRoute.to}
                  params={{ chatId: chat._id }}
                  className={navLinkClasses}
                  activeProps={{
                    className: activeNavLinkClasses,
                  }}
                >
                  <SidebarMenuButton className="pl-3">
                    <span className="flex flex-1 items-center gap-2 overflow-hidden">
                      <span
                        className={`size-2 shrink-0 rounded-full ${projectConfidenceColor(
                          chat.projectData?.forecast?.confidence,
                        )}`}
                      />
                      <span className="flex flex-1 flex-col overflow-hidden">
                        <Text
                          size="1"
                          className="truncate text-puntt-neutral-gray-11"
                        >
                          {chat.projectData?.name || chat.title}
                        </Text>
                        {!isOwnChat && chat.ownerName ? (
                          <Text
                            size="1"
                            className="truncate text-puntt-neutral-gray-10"
                            style={{ fontSize: "10px", lineHeight: "12px" }}
                            data-testid="sidebar-ask-puntt-project-owner"
                          >
                            {chat.ownerName}
                          </Text>
                        ) : null}
                      </span>
                    </span>
                  </SidebarMenuButton>
                </NavLink>
              </Link>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function AskPunttRecents() {
  const { data } = useAskPunttChats();
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const navigate = useNavigate();
  const deleteChatMutation = useDeleteAskPunttChat();
  const isOnAskPuntt = matches.some((m) => m.pathname.includes("/ask-puntt"));
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);

  if (!canAccessAskPuntt() || !open || !isOnAskPuntt || !data?.chats?.length)
    return null;

  const nonProjectChats =
    data.chats.filter((c) => c.workflowType !== "project") ?? [];
  if (nonProjectChats.length === 0) return null;

  const isActiveChat = (chatId: string) =>
    matches.some((m) => m.pathname === `/ask-puntt/${chatId}`);

  function handleDelete(chatId: string) {
    const currentPath = window.location.pathname;
    const isViewing = currentPath.includes(`/ask-puntt/${chatId}`);
    deleteChatMutation.mutate(chatId, {
      onSuccess: () => {
        setChatIdToDelete(null);
        if (isViewing) {
          navigate({ to: askPunttRoute.to });
        }
      },
    });
  }

  const chatToDelete = nonProjectChats.find((c) => c._id === chatIdToDelete);

  return (
    <>
      <div className="p-2" data-testid="sidebar-ask-puntt-chats">
        <div
          className="relative flex w-full items-center gap-4 rounded-md px-2 py-1.5"
          style={{ background: "inherit" }}
          data-testid="sidebar-ask-puntt-chats-header"
        >
          <Text size="2" weight="bold" className="flex-1 truncate px-0">
            Chats
          </Text>
          <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
            <Button
              size="1"
              variant="ghost"
              color="gray"
              className="hover:bg-puntt-neutral-gray-4 hover:shadow-sm"
              style={{ zIndex: 2 }}
              onClick={() => navigate({ to: askPunttRoute.to })}
              aria-label="New Chat"
              data-testid="sidebar-ask-puntt-new-chat"
            >
              +
            </Button>
          </div>
        </div>
        {nonProjectChats.slice(0, 8).map((chat) => {
          // Treat as own until auth resolves so the delete button doesn't
          // briefly disappear from own chats before currentUserId loads.
          const isOwnChat =
            currentUserId === undefined || chat.userId === currentUserId;
          return (
            <div
              key={chat._id}
              className={cx(
                "group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 pl-3 pr-2 transition-colors hover:bg-puntt-neutral-gray-3",
                isActiveChat(chat._id) &&
                  "bg-puntt-neutral-gray-3 hover:bg-puntt-neutral-gray-4",
              )}
              data-testid="sidebar-ask-puntt-chat-item"
              data-chat-id={chat._id}
            >
              <Link
                asChild
                size="2"
                weight="medium"
                className="puntt-link flex-1 overflow-hidden"
              >
                <NavLink
                  to={askPunttChatRoute.to}
                  params={{ chatId: chat._id }}
                  className="w-full overflow-hidden"
                >
                  <span className="block w-full truncate">{chat.title}</span>
                  {!isOwnChat && chat.ownerName ? (
                    <span
                      className="block w-full truncate text-puntt-neutral-gray-10"
                      style={{ fontSize: "11px", lineHeight: "14px" }}
                      data-testid="sidebar-ask-puntt-chat-owner"
                    >
                      {chat.ownerName}
                    </span>
                  ) : null}
                </NavLink>
              </Link>
              {isOwnChat ? (
                <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
                  <div
                    className="pointer-events-none -mr-2 hidden h-6 w-12 group-hover/chat-item:block"
                    style={{
                      background:
                        "linear-gradient(to left, rgb(var(--puntt-cool-gray-3)) 25%, transparent 100%)",
                      zIndex: 1,
                    }}
                  />
                  <IconButton
                    size="2"
                    variant="ghost"
                    color="gray"
                    className="hidden hover:bg-puntt-red-9 hover:text-base-white group-hover/chat-item:flex"
                    style={{ zIndex: 2 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setChatIdToDelete(chat._id);
                    }}
                    aria-label="Delete chat"
                    data-testid="sidebar-ask-puntt-delete-chat"
                  >
                    <Minus size={12} weight="bold" />
                  </IconButton>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <Dialog.Root
        open={chatIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setChatIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Delete chat?</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to delete{" "}
            {chatToDelete ? <em>{chatToDelete.title}</em> : "this chat"}?
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setChatIdToDelete(null)}
              data-testid="sidebar-delete-chat-cancel"
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => chatIdToDelete && handleDelete(chatIdToDelete)}
              disabled={deleteChatMutation.isPending}
              loading={deleteChatMutation.isPending}
              data-testid="sidebar-delete-chat-confirm"
            >
              <Trash />
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
